import { integrationApi } from ".";
import { Configuration } from "@/config";
import {
  BooleanOperator,
  Comparison,
  PageRequest,
  PagedResponse,
  PropertyFilter,
} from "@/api/types";

const {
  config: { PCS_SOFTWARE_CUSTOMER_ID },
} = new Configuration();

const customerHeader = () => ({
  headers: { "Customer-Id": PCS_SOFTWARE_CUSTOMER_ID.toString() },
});

/** Build a PageRequest with optional equality filters. */
const buildPageRequest = (
  filters?: { field: string; value: string }[],
  pageNumber?: number,
  pageSize?: number
): PageRequest => {
  const req: PageRequest = {};
  if (pageNumber) req.pageNumber = pageNumber;
  if (pageSize) req.pageSize = pageSize;
  if (filters && filters.length > 0) {
    req.filterSet = {
      operator: filters.length > 1 ? BooleanOperator.And : BooleanOperator.None,
      filters: filters.map(
        (f): PropertyFilter => ({
          comparison: Comparison.Equals,
          field: f.field,
          value: f.value,
        })
      ),
    };
  }
  return req;
};

// ── Integration Catalog ──

export interface IntegrationDto {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationRequest {
  code: string;
  name: string;
  isActive?: boolean;
}

export interface UpdateIntegrationRequest {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
}

export const listIntegrations = async (
  pageNumber?: number,
  pageSize?: number,
  isActive?: boolean
) => {
  const filters = isActive !== undefined
    ? [{ field: "IsActive", value: isActive.toString() }]
    : undefined;
  const response = await integrationApi.post<PagedResponse<IntegrationDto>>(
    "v1/integrations/list",
    buildPageRequest(filters, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const getIntegration = async (id: number) => {
  const response = await integrationApi.get<IntegrationDto>(
    `v1/integrations/${id}`,
    customerHeader()
  );
  return response?.data;
};

export const createIntegration = async (req: AddIntegrationRequest) => {
  const response = await integrationApi.post<number>(
    "v1/integrations",
    req,
    customerHeader()
  );
  return response?.data;
};

export const updateIntegration = async (req: UpdateIntegrationRequest) => {
  const response = await integrationApi.put<boolean>(
    "v1/integrations",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Integration Tags ──

export interface IntegrationTagDto {
  id: number;
  name: string;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationTagRequest {
  name: string;
  isActive?: boolean;
}

export interface UpdateIntegrationTagRequest {
  id: number;
  name: string;
  isActive: boolean;
}

export const listIntegrationTags = async (
  pageNumber?: number,
  pageSize?: number
) => {
  const response = await integrationApi.post<PagedResponse<IntegrationTagDto>>(
    "v1/integrations/tags/list",
    buildPageRequest(undefined, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const createIntegrationTag = async (req: AddIntegrationTagRequest) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/tags",
    req,
    customerHeader()
  );
  return response?.data;
};

export const updateIntegrationTag = async (
  req: UpdateIntegrationTagRequest
) => {
  const response = await integrationApi.put<boolean>(
    "v1/integrations/tags",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Integration Tag Mappings ──

export interface IntegrationTagMappingDto {
  id: number;
  integrationId: number;
  integrationTagId: number;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationTagMappingRequest {
  integrationId: number;
  integrationTagId: number;
}

export const listIntegrationTagMappings = async (
  integrationId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters = integrationId !== undefined
    ? [{ field: "IntegrationId", value: integrationId.toString() }]
    : undefined;
  const response = await integrationApi.post<
    PagedResponse<IntegrationTagMappingDto>
  >(
    "v1/integrations/tag-mappings/list",
    buildPageRequest(filters, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const createIntegrationTagMapping = async (
  req: AddIntegrationTagMappingRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/tag-mappings",
    req,
    customerHeader()
  );
  return response?.data;
};

export const deleteIntegrationTagMapping = async (id: number) => {
  const response = await integrationApi.delete<boolean>(
    `v1/integrations/tag-mappings/${id}`,
    customerHeader()
  );
  return response?.data;
};

// ── Customer Default Integrations ──

export interface CustomerDefaultIntegrationDto {
  id: number;
  customerId: number;
  integrationTagId: number;
  integrationId: number;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface SetupCustomerDefaultIntegrationRequest {
  customerId?: number | null;
  integrationTagId: number;
  integrationId: number;
  isActive?: boolean;
}

export const listCustomerDefaultIntegrations = async (
  customerId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters = customerId !== undefined
    ? [{ field: "CustomerId", value: customerId.toString() }]
    : undefined;
  const response = await integrationApi.post<
    PagedResponse<CustomerDefaultIntegrationDto>
  >(
    "v1/integrations/customer-defaults/list",
    buildPageRequest(filters, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const setupCustomerDefaultIntegration = async (
  req: SetupCustomerDefaultIntegrationRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/customer-defaults",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Integration Configurations ──

export interface IntegrationConfigurationDto {
  id: number;
  integrationId: number;
  scopeType: number;
  customerId: number | null;
  userId: number | null;
  authType: number;
  apiKey: string | null;
  username: string | null;
  password: string | null;
  companyCode: string | null;
  configJson: string | null;
  configName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  isTestMode: boolean | null;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationConfigurationRequest {
  integrationId: number;
  scopeType: number;
  customerId?: number | null;
  userId?: number | null;
  authType: number;
  apiKey?: string | null;
  username?: string | null;
  password?: string | null;
  companyCode?: string | null;
  configJson?: string | null;
  configName?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive?: boolean;
  isTestMode?: boolean | null;
}

export interface UpdateIntegrationConfigurationRequest {
  id: number;
  integrationId: number;
  scopeType: number;
  customerId?: number | null;
  userId?: number | null;
  authType: number;
  apiKey?: string | null;
  username?: string | null;
  password?: string | null;
  companyCode?: string | null;
  configJson?: string | null;
  configName?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  isTestMode?: boolean | null;
}

export const listIntegrationConfigurations = async (
  integrationId?: number,
  scopeType?: number,
  customerId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters: { field: string; value: string }[] = [];
  if (integrationId !== undefined)
    filters.push({ field: "IntegrationId", value: integrationId.toString() });
  if (scopeType !== undefined)
    filters.push({ field: "ScopeType", value: scopeType.toString() });
  if (customerId !== undefined)
    filters.push({ field: "CustomerId", value: customerId.toString() });
  const response = await integrationApi.post<
    PagedResponse<IntegrationConfigurationDto>
  >(
    "v1/integrations/configurations/list",
    buildPageRequest(filters.length > 0 ? filters : undefined, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const getIntegrationConfiguration = async (id: number) => {
  const response = await integrationApi.get<IntegrationConfigurationDto>(
    `v1/integrations/configurations/${id}`,
    customerHeader()
  );
  return response?.data;
};

export const createIntegrationConfiguration = async (
  req: AddIntegrationConfigurationRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/configurations",
    req,
    customerHeader()
  );
  return response?.data;
};

export const updateIntegrationConfiguration = async (
  req: UpdateIntegrationConfigurationRequest
) => {
  const response = await integrationApi.put<boolean>(
    "v1/integrations/configurations",
    req,
    customerHeader()
  );
  return response?.data;
};

export const deleteIntegrationConfiguration = async (id: number) => {
  const response = await integrationApi.delete<boolean>(
    `v1/integrations/configurations/${id}`,
    customerHeader()
  );
  return response?.data;
};

// ── Integration Configuration Properties ──

export interface IntegrationConfigurationPropertyDto {
  id: number;
  integrationConfigurationId: number;
  propertyName: string;
  propertyValue: string;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationConfigurationPropertyRequest {
  integrationConfigurationId: number;
  propertyName: string;
  propertyValue: string;
}

export interface UpdateIntegrationConfigurationPropertyRequest {
  id: number;
  integrationConfigurationId: number;
  propertyName: string;
  propertyValue: string;
}

export const listConfigurationProperties = async (
  integrationConfigurationId: number
) => {
  const response = await integrationApi.get<
    IntegrationConfigurationPropertyDto[]
  >(
    `v1/integrations/configurations/properties/${integrationConfigurationId}`,
    customerHeader()
  );
  return response?.data;
};

export const createConfigurationProperty = async (
  req: AddIntegrationConfigurationPropertyRequest
) => {
  const response = await integrationApi.post<IntegrationConfigurationPropertyDto>(
    "v1/integrations/configurations/properties",
    req,
    customerHeader()
  );
  return response?.data;
};

export const updateConfigurationProperty = async (
  req: UpdateIntegrationConfigurationPropertyRequest
) => {
  const response = await integrationApi.put<boolean>(
    "v1/integrations/configurations/properties",
    req,
    customerHeader()
  );
  return response?.data;
};

export const deleteConfigurationProperty = async (id: number) => {
  const response = await integrationApi.delete<boolean>(
    `v1/integrations/configurations/properties/${id}`,
    customerHeader()
  );
  return response?.data;
};

// ── Customer Integration Settings ──

export interface CustomerIntegrationSettingDto {
  id: number;
  customerId: number;
  integrationId: number;
  usesMasterConfig: boolean;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface SetupCustomerIntegrationSettingRequest {
  customerId?: number | null;
  integrationId: number;
  usesMasterConfig?: boolean;
  isActive?: boolean;
}

export const listCustomerIntegrationSettings = async (
  integrationId?: number,
  customerId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters: { field: string; value: string }[] = [];
  if (integrationId !== undefined)
    filters.push({ field: "IntegrationId", value: integrationId.toString() });
  if (customerId !== undefined)
    filters.push({ field: "CustomerId", value: customerId.toString() });
  const response = await integrationApi.post<
    PagedResponse<CustomerIntegrationSettingDto>
  >(
    "v1/integrations/configurations/settings/list",
    buildPageRequest(filters.length > 0 ? filters : undefined, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const setupCustomerIntegrationSetting = async (
  req: SetupCustomerIntegrationSettingRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/configurations/settings",
    req,
    customerHeader()
  );
  return response?.data;
};

export const deleteCustomerIntegrationSetting = async (id: number) => {
  const response = await integrationApi.delete<boolean>(
    `v1/integrations/configurations/settings/${id}`,
    customerHeader()
  );
  return response?.data;
};

// ── Default Pricing ──

export interface IntegrationDefaultPricingDto {
  id: number;
  integrationId: number;
  currencyCode: string;
  pricePerUse: number;
  includedUses: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationDefaultPricingRequest {
  integrationId: number;
  currencyCode: string;
  pricePerUse: number;
  includedUses: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive?: boolean;
}

export const listDefaultPricing = async (
  integrationId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters = integrationId !== undefined
    ? [{ field: "IntegrationId", value: integrationId.toString() }]
    : undefined;
  const response = await integrationApi.post<
    PagedResponse<IntegrationDefaultPricingDto>
  >(
    "v1/integrations/billing/default-pricing/list",
    buildPageRequest(filters, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const createDefaultPricing = async (
  req: AddIntegrationDefaultPricingRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/billing/default-pricing",
    req,
    customerHeader()
  );
  return response?.data;
};

export interface UpdateIntegrationDefaultPricingRequest {
  id: number;
  integrationId: number;
  currencyCode: string;
  pricePerUse: number;
  includedUses: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
}

export const updateDefaultPricing = async (
  req: UpdateIntegrationDefaultPricingRequest
) => {
  const response = await integrationApi.put<boolean>(
    "v1/integrations/billing/default-pricing",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Customer Pricing ──

export interface CustomerIntegrationPricingDto {
  id: number;
  customerId: number;
  integrationId: number;
  currencyCode: string;
  pricePerUse: number;
  includedUses: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface SetupCustomerIntegrationPricingRequest {
  customerId?: number | null;
  integrationId: number;
  currencyCode: string;
  pricePerUse: number;
  includedUses: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive?: boolean;
}

export const listCustomerPricing = async (
  integrationId?: number,
  customerId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters: { field: string; value: string }[] = [];
  if (integrationId !== undefined)
    filters.push({ field: "IntegrationId", value: integrationId.toString() });
  if (customerId !== undefined)
    filters.push({ field: "CustomerId", value: customerId.toString() });
  const response = await integrationApi.post<
    PagedResponse<CustomerIntegrationPricingDto>
  >(
    "v1/integrations/billing/customer-pricing/list",
    buildPageRequest(filters.length > 0 ? filters : undefined, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const setupCustomerPricing = async (
  req: SetupCustomerIntegrationPricingRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/billing/customer-pricing",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Usage Events ──

export interface IntegrationUsageEventDto {
  id: number;
  customerId: number;
  integrationId: number;
  userId: number | null;
  usedMasterConfig: boolean;
  integrationConfigurationId: number | null;
  usageType: string;
  units: number;
  occurredAt: string;
  correlationId: string | null;
  metadataJson: string | null;
  isActive: boolean;
  createdById: number;
  createdAtUtc: string;
  updatedById: number;
  updatedAtUtc: string;
}

export interface AddIntegrationUsageEventRequest {
  customerId?: number | null;
  integrationId: number;
  userId?: number | null;
  usedMasterConfig: boolean;
  integrationConfigurationId?: number | null;
  usageType: string;
  units?: number;
  occurredAt?: string | null;
  correlationId?: string | null;
  metadataJson?: string | null;
}

export const listUsageEvents = async (
  integrationId?: number,
  customerId?: number,
  userId?: number,
  pageNumber?: number,
  pageSize?: number
) => {
  const filters: { field: string; value: string }[] = [];
  if (integrationId !== undefined)
    filters.push({ field: "IntegrationId", value: integrationId.toString() });
  if (customerId !== undefined)
    filters.push({ field: "CustomerId", value: customerId.toString() });
  if (userId !== undefined)
    filters.push({ field: "UserId", value: userId.toString() });
  const response = await integrationApi.post<
    PagedResponse<IntegrationUsageEventDto>
  >(
    "v1/integrations/billing/usage/list",
    buildPageRequest(filters.length > 0 ? filters : undefined, pageNumber, pageSize),
    customerHeader()
  );
  return response?.data;
};

export const createUsageEvent = async (
  req: AddIntegrationUsageEventRequest
) => {
  const response = await integrationApi.post<number>(
    "v1/integrations/billing/usage",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Usage Summary ──

export interface IntegrationUsageSummaryDto {
  integrationId: number;
  customerId: number;
  usageType: string;
  usedMasterConfig: boolean;
  totalUnits: number;
  eventCount: number;
  earliestOccurrence: string;
  latestOccurrence: string;
  pricePerUse: number | null;
  includedUses: number | null;
  currencyCode: string | null;
  estimatedCost: number | null;
}

export interface GetUsageSummaryRequest {
  dateFrom: string;
  dateTo: string;
  integrationId?: number;
  customerId?: number;
}

export const getUsageSummary = async (req: GetUsageSummaryRequest) => {
  const response = await integrationApi.post<IntegrationUsageSummaryDto[]>(
    "v1/integrations/billing/usage/summary",
    req,
    customerHeader()
  );
  return response?.data;
};

// ── Integration Audits ──

export interface IntegrationAuditDto {
  id: number;
  entityType: string;
  entityId: number;
  operationPayload: string;
  userId: number;
  dateModifiedUtc: string;
}

export interface GetIntegrationAuditsRequest {
  entityType?: string;
  userId?: number;
  pageNumber?: number;
  pageSize?: number;
}

export const getIntegrationAudits = async (
  req: GetIntegrationAuditsRequest
) => {
  const response = await integrationApi.post<IntegrationAuditDto[]>(
    "v1/integrations/audits/list",
    req,
    customerHeader()
  );
  return response?.data;
};
